import { useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, toolSuccess, toolError } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { PdfPagePreview } from '@/features/pdf-page-preview'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_FILE_MB,
  POSITIONS,
  drawPageNumber,
  validateFileSize,
  numberedFilename,
  type PageNumberPosition,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('numeraciyaStranic').analyticsKey

// ─── State machine ────────────────────────────────────────────────────────────

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'loading'; file: File }
  | { kind: 'success'; file: File; blob: Blob; objectUrl: string; totalPages: number }
  | { kind: 'error'; file: File | null; message: string; errorCode: string }

// ─── Drop-zone ────────────────────────────────────────────────────────────────

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

// ─── File badge ───────────────────────────────────────────────────────────────

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

// ─── Position selector ────────────────────────────────────────────────────────

interface PositionSelectorProps {
  value: PageNumberPosition
  onChange: (v: PageNumberPosition) => void
  disabled: boolean
}

function PositionSelector({ value, onChange, disabled }: PositionSelectorProps) {
  const t = useT()
  const labels: Record<PageNumberPosition, string> = {
    'bottom-center': t.numeraciyaStranic.positionBottomCenter,
    'bottom-right': t.numeraciyaStranic.positionBottomRight,
    'top-right': t.numeraciyaStranic.positionTopRight,
  }

  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-foreground">{t.numeraciyaStranic.positionLegend}</legend>
      <div className="grid grid-cols-3 gap-2">
        {POSITIONS.map((pos) => {
          const selected = value === pos
          return (
            <label
              key={pos}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-lg border p-3 text-sm font-medium transition-colors',
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary text-foreground'
                  : 'border-border text-foreground hover:border-primary/40 hover:bg-accent/20',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <input
                type="radio"
                name="page-number-position"
                value={pos}
                checked={selected}
                onChange={() => onChange(pos)}
                className="sr-only"
              />
              {labels[pos]}
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

// ─── Start number input ───────────────────────────────────────────────────────

interface NumberInputProps {
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  label: string
  disabled: boolean
}

function NumberInput({ value, onChange, min, max, label, disabled }: NumberInputProps) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const n = Number(e.target.value)
          if (!isNaN(n) && n >= min && n <= max) {
            onChange(n)
          }
        }}
        disabled={disabled}
        className={cn(
          'block w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
          'placeholder:text-muted-foreground/70',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
          disabled && 'pointer-events-none opacity-50',
        )}
      />
    </div>
  )
}

interface NumberingPreviewProps {
  file: File
  focusedPage: number
  startPage: number
  startNumber: number
  position: PageNumberPosition
  onDocumentLoad: (totalPages: number) => void
}

function NumberingPreview({
  file,
  focusedPage,
  startPage,
  startNumber,
  position,
  onDocumentLoad,
}: NumberingPreviewProps) {
  return (
    <PdfPagePreview
      file={file}
      maxPages={1}
      pageNumbers={[focusedPage]}
      onDocumentLoad={onDocumentLoad}
      showLimitNotice={false}
      renderOverlay={(thumbnail) => {
        if (thumbnail.pageNumber < startPage) return null
        const pageNumber = startNumber + thumbnail.pageNumber - startPage

        return (
          <span
            className={cn(
              'pointer-events-none absolute z-10 rounded bg-primary/85 px-2 py-1',
              'text-xs font-semibold tabular-nums text-primary-foreground shadow-sm',
              pageNumberPositionClassName(position),
            )}
          >
            {pageNumber}
          </span>
        )
      }}
    />
  )
}

function pageNumberPositionClassName(position: PageNumberPosition): string {
  if (position === 'top-right') return 'right-4 top-4'
  if (position === 'bottom-right') return 'bottom-4 right-4'
  return 'bottom-4 left-1/2 -translate-x-1/2'
}

// ─── Result panel ─────────────────────────────────────────────────────────────

interface ResultPanelProps {
  file: File
  blob: Blob
  objectUrl: string
  totalPages: number
  onReset: () => void
}

function ResultPanel({ file, blob, objectUrl, totalPages, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = numberedFilename(file.name)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.numeraciyaStranic.totalPages}</span>
            <span className="font-medium text-foreground">{totalPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.numeraciyaStranic.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Error panel ──────────────────────────────────────────────────────────────

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

// ─── Main form ────────────────────────────────────────────────────────────────

export function NumeraciyaStranicForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [position, setPosition] = useState<PageNumberPosition>('bottom-center')
  const [startNumber, setStartNumber] = useState(1)
  const [startPage, setStartPage] = useState(1)
  const [previewTotalPages, setPreviewTotalPages] = useState<number | null>(null)

  const isLoading = phase.kind === 'loading'
  const hasFile = phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
  const currentFile = hasFile ? phase.file : null

  function handleFile(file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large', { position, start_number: startNumber, start_page: startPage })
      setPhase({
        kind: 'error',
        file,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }

    setPhase({ kind: 'ready', file })
    setPreviewTotalPages(null)
  }

  function handleClear() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    setPhase({ kind: 'idle' })
    setPreviewTotalPages(null)
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const file = phase.file

    setPhase({ kind: 'loading', file })
    toolInvoke(UTILITY, { position, start_number: startNumber, start_page: startPage })

    const startMs = Date.now()

    try {
      const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib')
      const bytes = await file.arrayBuffer()
      const pdfDoc = await PDFDocument.load(bytes)
      const pages = pdfDoc.getPages()
      const totalPages = pages.length

      if (startPage > totalPages) {
        const code = 'start_page_out_of_range'
        toolError(UTILITY, code, {
          position,
          start_number: startNumber,
          start_page: startPage,
          total_pages: totalPages,
        })
        setPhase({
          kind: 'error',
          file,
          errorCode: code,
          message: format(t.numeraciyaStranic.startPageTooHigh, { pages: totalPages }),
        })
        return
      }

      const font = await pdfDoc.embedFont(StandardFonts.Helvetica)

      const pagesToNumber = totalPages - startPage + 1

      for (let i = startPage - 1; i < totalPages; i++) {
        const page = pages[i]
        if (!page) continue

        const pageNumber = startNumber + (i - (startPage - 1))
        drawPageNumber(page, pageNumber, position, font, rgb(0.4, 0.4, 0.4))
      }

      const out = await pdfDoc.save()
      const blob = new Blob([out], { type: 'application/pdf' })
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, durationMs, {
        position,
        start_number: startNumber,
        start_page: startPage,
        total_pages: totalPages,
        numbered_pages: pagesToNumber,
        output_bytes: blob.size,
      })

      setPhase({
        kind: 'success',
        file,
        blob,
        objectUrl,
        totalPages,
      })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      const message = code === 'encrypted' ? t.numeraciyaStranic.encrypted : t.numeraciyaStranic.parseError
      toolError(UTILITY, code, { position, start_number: startNumber, start_page: startPage })
      setPhase({ kind: 'error', file, errorCode: code, message })
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          file={phase.file}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          totalPages={phase.totalPages}
          onReset={handleClear}
        />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <PositionSelector value={position} onChange={setPosition} disabled={isLoading} />

      <div className="grid grid-cols-2 gap-4">
        <NumberInput
          value={startNumber}
          onChange={setStartNumber}
          min={1}
          max={99999}
          label={t.numeraciyaStranic.startNumberLabel}
          disabled={isLoading}
        />
        <NumberInput
          value={startPage}
          onChange={setStartPage}
          min={1}
          max={99999}
          label={t.numeraciyaStranic.startPageLabel}
          disabled={isLoading}
        />
      </div>

      {currentFile && (
        <NumberingPreview
          file={currentFile}
          focusedPage={
            previewTotalPages === null
              ? Math.max(1, startPage)
              : Math.min(Math.max(1, startPage), previewTotalPages)
          }
          startPage={startPage}
          startNumber={startNumber}
          position={position}
          onDocumentLoad={setPreviewTotalPages}
        />
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="sticky bottom-3 z-10 w-full shadow-lg"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.numeraciyaStranic.loading}
          </>
        ) : (
          t.numeraciyaStranic.submit
        )}
      </Button>
    </div>
  )
}
